import { useState, useEffect } from 'react';
import { useNavigate, Link, useSearchParams, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { toast } from 'sonner';
import { Wrench, Eye, EyeOff, AlertTriangle } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = window.location.origin;
const API = `${BACKEND_URL}/api`;

export default function Login() {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const { login, user, isLoading: authLoading } = useAuth();
  
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [siteIdentity, setSiteIdentity] = useState(null);
  const [maintenance, setMaintenance] = useState(null);
  
  const [formData, setFormData] = useState({
    identifier: '', // email or phone
    password: ''
  });

  // Redirect if already logged in (for PWA persistence)
  useEffect(() => {
    if (!authLoading && user) {
      const redirectTo = searchParams.get('redirect');
      if (redirectTo) {
        navigate(redirectTo, { replace: true });
      } else {
        // Redirect based on role
        switch (user.role) {
          case 'admin':
          case 'manager':
            navigate('/admin', { replace: true });
            break;
          case 'provider':
            navigate('/provider/home', { replace: true });
            break;
          default:
            navigate('/home', { replace: true });
        }
      }
    }
  }, [user, authLoading, navigate, searchParams]);

  useEffect(() => {
    // Fetch site identity for logo
    axios.get(`${API}/cms/landing`)
      .then(res => {
        if (res.data?.site_identity) {
          setSiteIdentity(res.data.site_identity);
        }
      })
      .catch(() => {});
      
    // Check maintenance status
    axios.get(`${API}/config/maintenance`)
      .then(res => setMaintenance(res.data))
      .catch(() => {});
      
    // Show message if redirected from forced logout
    if (location.state?.message) {
      toast.error(location.state.message);
    }
  }, [location.state]);

  const handleChange = (e) => {
    setFormData(prev => ({
      ...prev,
      [e.target.name]: e.target.value
    }));
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    
    if (!formData.identifier || !formData.password) {
      toast.error('Please enter your email/phone and password');
      return;
    }
    
    setIsLoading(true);
    
    try {
      // Determine if identifier is email or phone
      const isEmail = formData.identifier.includes('@');
      
      const credentials = isEmail 
        ? { email: formData.identifier, password: formData.password }
        : { phone: formData.identifier, password: formData.password };
      
      const user = await login(credentials);
      
      toast.success('Welcome back!');
      
      // Check for redirect parameter
      const redirectTo = searchParams.get('redirect');
      if (redirectTo) {
        navigate(redirectTo);
        return;
      }
      
      // Redirect based on role
      if (user.role === 'admin' || user.role === 'manager') navigate('/admin');
      else if (user.role === 'provider') navigate('/provider/home');
      else navigate('/home');
    } catch (error) {
      // Handle deactivation - the popup will show automatically via AuthContext
      if (error.message === 'ACCOUNT_DEACTIVATED') {
        // Don't show error toast, the popup handles this
        return;
      }
      toast.error(error.response?.data?.detail || 'Invalid credentials');
    } finally {
      setIsLoading(false);
    }
  };

  // Show loading while checking authentication
  if (authLoading) {
    return (
      <div className="min-h-screen bg-stone-50 flex items-center justify-center">
        <div className="text-center">
          <div className="w-8 h-8 border-4 border-amber-500 border-t-transparent rounded-full animate-spin mx-auto mb-3"></div>
          <p className="text-slate-600 text-sm">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-stone-50 flex flex-col">
      {/* Header */}
      <header className="p-4">
        <Link to="/" className="inline-flex items-center text-slate-600 hover:text-slate-900">
          <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
          Back
        </Link>
      </header>

      <div className="flex-1 flex items-center justify-center px-4 py-8">
        <div className="w-full max-w-md">
          {/* Logo */}
          <div className="text-center mb-8">
            {siteIdentity?.site_logo ? (
              <img 
                src={siteIdentity.site_logo.startsWith('http') 
                    ? siteIdentity.site_logo 
                    : `${BACKEND_URL}${siteIdentity.site_logo}`
                } 
                alt={siteIdentity?.site_title || 'HandyBayani'} 
                className="h-16 w-16 object-contain mx-auto mb-4 rounded-2xl"
              />
            ) : (
              <div className="w-16 h-16 bg-slate-900 rounded-2xl flex items-center justify-center mx-auto mb-4 p-2">
                <img src="/handybayani-logo.png" alt="HandyBayani" className="w-12 h-12 object-contain" />
              </div>
            )}
            <h1 className="text-2xl font-bold text-slate-900 font-[Poppins]">
              {siteIdentity?.site_title || 'HandyBayani'}
            </h1>
            <p className="text-slate-500 mt-1">Sign in to your account</p>
          </div>

          {/* Maintenance Banner */}
          {maintenance?.enabled && (
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 mb-4">
              <div className="flex items-start gap-3">
                <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm font-medium text-amber-800">Site Under Maintenance</p>
                  <p className="text-sm text-amber-700 mt-1">
                    Only administrators can sign in at this time. Customer and provider access is temporarily disabled.
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Login Form */}
          <form onSubmit={handleLogin} className="space-y-4" data-testid="login-form">
            <div>
              <Label htmlFor="identifier">Email or Phone Number</Label>
              <Input
                id="identifier"
                name="identifier"
                type="text"
                placeholder="you@example.com or 09171234567"
                value={formData.identifier}
                onChange={handleChange}
                className="h-12 mt-1.5"
                required
                data-testid="login-email-input"
              />
            </div>
            
            <div>
              <Label htmlFor="password">Password</Label>
              <div className="relative mt-1.5">
                <Input
                  id="password"
                  name="password"
                  type={showPassword ? 'text' : 'password'}
                  placeholder="Enter your password"
                  value={formData.password}
                  onChange={handleChange}
                  className="h-12 pr-12"
                  required
                  data-testid="login-password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                >
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
              <div className="text-right mt-2">
                <Link to="/forgot-password" className="text-sm text-amber-600 hover:underline" data-testid="forgot-password-link">
                  Forgot password?
                </Link>
              </div>
            </div>
            
            <Button 
              type="submit" 
              className="w-full h-12 bg-slate-900 hover:bg-slate-800"
              disabled={isLoading}
              data-testid="login-submit-btn"
            >
              {isLoading ? 'Signing in...' : 'Sign In'}
            </Button>
          </form>

          {/* Register Link */}
          <p className="text-center text-sm text-slate-500 mt-6">
            Don't have an account?{' '}
            <Link to="/register" className="text-amber-600 hover:underline font-medium">
              Sign up
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}
